import { memo, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useChartSelections from "components/charting/hooks/useChartSelections";
import useOneRepMaxSourceQueryState, { OneRepMaxSource } from "components/charting/hooks/useOneRepMaxSourceQueryState";
import ThemeProvider2024 from "components/MuiThemeProvider/ThemeProvider2024";
import ToggleButtonGroupOptions from "components/ToggleButtonGroupOptions";
import Typography from "components/Typography";
import ExerciseHelpers from "helpers/ExerciseHelpers";
import LocalStorageHelper from "helpers/LocalStorageHelper";
import useVelocityOnBoarding from "hooks/useVelocityOnBoarding";
import { MetadataType } from "types/metadata";
import OneRepMaxVelocityOnboardingTooltip from "../OneRepMaxVelocityOnboardingTooltip";

function TooltipTitle() {
  const { t } = useTranslation();
  return (
    <Typography variant="TextXS">
      {t("athlete_profile_chart_controls.one_rep_max_source_velocity_unavailable_tooltip")}
    </Typography>
  );
}

function OneRepMaxSourceToggle() {
  const { t } = useTranslation();
  const [source, setSource] = useOneRepMaxSourceQueryState();
  const { measures } = useChartSelections();
  const { open: onboardingOpen } = useVelocityOnBoarding(LocalStorageHelper.keys.showAthleteOneRepMaxVelocityTooltip);

  const measure = measures[0];
  const measureSelected = Boolean(measure?.id);

  const velocityDisabled =
    measureSelected &&
    !(measure.type === MetadataType.EXERCISE && ExerciseHelpers.isLvpCapableExercise(measure.metadata));

  useEffect(() => {
    if (velocityDisabled && source === OneRepMaxSource.Velocity) {
      setSource(OneRepMaxSource.Manual);
    }
  }, [setSource, source, velocityDisabled]);

  const options = useMemo(
    () => [
      {
        value: OneRepMaxSource.Velocity,
        label: t("athlete_profile_chart_controls.one_rep_max_source_velocity_option"),
        // Left undefined when available so the group's `disabled` still applies.
        disabled: velocityDisabled || undefined,
        "data-highlight": onboardingOpen && !velocityDisabled,
        slotProps: {
          tooltip: velocityDisabled ? { title: <TooltipTitle /> } : undefined,
        },
      },
      { value: OneRepMaxSource.Manual, label: t("athlete_profile_chart_controls.one_rep_max_source_manual_option") },
    ],
    [onboardingOpen, t, velocityDisabled]
  );

  const handleOnChange = useCallback(
    (_event: React.SyntheticEvent, value: OneRepMaxSource | null) => {
      if (value !== null) setSource(value);
    },
    [setSource]
  );

  return (
    <ThemeProvider2024>
      <OneRepMaxVelocityOnboardingTooltip disabled={velocityDisabled}>
        <ToggleButtonGroupOptions
          value={source}
          onChange={handleOnChange}
          exclusive
          options={options}
          sx={{ "& .MuiToggleButton-root": { lineHeight: "20px" } }}
          aria-label={t("athlete_profile_chart_controls.one_rep_max_source_toggle_label")}
          disabled={!measureSelected}
        />
      </OneRepMaxVelocityOnboardingTooltip>
    </ThemeProvider2024>
  );
}

export default memo(OneRepMaxSourceToggle);
